import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiGet, apiPost, apiPut, apiDelete, apiPatch } from '../api/client';
import type {
  VideoListResponse,
  VideoResponse,
  VideoStorageResponse,
  VideoUploadTokenResponse,
  RequestVideoUploadTokenRequest,
} from '../types/api';
import { VideoState, VaultRole } from '../types/api';
import { parseServerDate } from '../utils/dateUtils';
import { filterVideos, isSharedVideo } from '../utils/videoLibrary';
import LettersList from './LettersList';
import SortableList from './SortableList';
import AlertMessage from './AlertMessage';
import LoadingSpinner from './LoadingSpinner';
import EmptyState from './EmptyState';
import './LegacyMessagesList.css';

interface Props {
  vaultId: string;
  userRole: VaultRole;
  onCountChange?: (count: number) => void;
}

// ── State icons ───────────────────────────────────────────────────────────────

function DraftVideoIcon() {
  return (
    <svg className="lmv-lock-icon lmv-lock-icon--draft" width="18" height="18"
         viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polygon points="23 7 16 12 23 17 23 7"/>
      <rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
    </svg>
  );
}

function SealedVideoIcon() {
  return (
    <svg className="lmv-lock-icon lmv-lock-icon--sealed" width="18" height="18"
         viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="11" width="18" height="11" rx="2"/>
      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
    </svg>
  );
}

function UnsealedVideoIcon() {
  return (
    <svg className="lmv-lock-icon lmv-lock-icon--unsealed" width="18" height="18"
         viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="11" width="18" height="11" rx="2"/>
      <path d="M7 11V7a5 5 0 0 1 9.9-1"/>
    </svg>
  );
}

// ── Vote progress bar ─────────────────────────────────────────────────────────

function VideoVoteBar({ yesVotes, totalMembers }: { yesVotes: number; totalMembers: number }) {
  if (totalMembers === 0) {
    return (
      <p className="lmv-vote-bar-label lmv-vote-bar-label--nudge">
        Invite family members to enable voting
      </p>
    );
  }
  const needed = Math.ceil(totalMembers / 2);
  const thresholdPct = (needed / totalMembers) * 100;
  const fillPct = Math.min((yesVotes / totalMembers) * 100, 100);
  const remaining = Math.max(needed - yesVotes, 0);
  return (
    <div className="lmv-vote-bar-wrap">
      <div className="lmv-vote-bar-track" role="progressbar"
           aria-valuenow={yesVotes} aria-valuemin={0} aria-valuemax={totalMembers}>
        <div className="lmv-vote-bar-fill" style={{ width: `${fillPct}%` }} />
        <div className="lmv-vote-bar-threshold" style={{ left: `${thresholdPct}%` }}
             title={`${needed} votes needed`} />
      </div>
      <p className="lmv-vote-bar-label">
        {yesVotes} of {totalMembers} {yesVotes === 1 ? 'member has' : 'members have'} voted to unseal
      </p>
      <p className="lmv-vote-bar-needed">
        {remaining > 0
          ? `${remaining} more ${remaining === 1 ? 'vote' : 'votes'} needed to unseal`
          : '✓ Threshold reached'}
      </p>
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDate(iso: string | null | undefined): string {
  return parseServerDate(iso)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) ?? '';
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

const MAX_VIDEO_BYTES = 1024 * 1024 * 1024; // 1 GB
const ACCEPTED_TYPES = 'video/mp4,video/quicktime,video/webm,video/x-matroska,.mp4,.mov,.webm,.mkv';

function doXhrUpload(
  sasUrl: string,
  file: File,
  onProgress: (pct: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Upload failed (HTTP ${xhr.status}). Please try again.`));
    };
    xhr.onerror = () => reject(new Error('Network error during upload. Please check your connection.'));
    xhr.open('PUT', sasUrl);
    xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    xhr.send(file);
  });
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function LegacyMessagesList({ vaultId, userRole, onCountChange }: Props) {
  const onCountChangeRef = useRef(onCountChange);
  onCountChangeRef.current = onCountChange;

  const canWrite = userRole === VaultRole.Owner || userRole === VaultRole.Spouse;

  const [activeTab, setActiveTab] = useState<'videos' | 'letters'>('videos');
  const [letterCount, setLetterCount] = useState(0);

  // Video state
  const [videos, setVideos] = useState<VideoResponse[]>([]);
  const [videosLoading, setVideosLoading] = useState(true);
  const [storage, setStorage] = useState<VideoStorageResponse | null>(null);
  const [error, setError] = useState('');

  // Upload form state
  const [showUpload, setShowUpload] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadDesc, setUploadDesc] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState('');
  const [uploadQuotaExceeded, setUploadQuotaExceeded] = useState(false);

  // Card state
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [playbackUrls, setPlaybackUrls] = useState<Record<string, string>>({});
  const [playbackLoading, setPlaybackLoading] = useState<string | null>(null);
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<VideoResponse | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Search + edit state
  const [query, setQuery] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');

  // Count bridge refs for combined tab badge
  const videoCountRef = useRef(0);
  const letterCountRef = useRef(0);
  const fileRef = useRef<HTMLInputElement>(null);

  function reportCount() {
    onCountChangeRef.current?.(videoCountRef.current + letterCountRef.current);
  }

  // ── Data loading ────────────────────────────────────────────────────────────

  const loadVideos = useCallback(async () => {
    try {
      const data = await apiGet<VideoListResponse>(`/api/vault/${vaultId}/legacy-videos`);
      setVideos(data.videos);
      // In preview mode canWrite is false — mirror server-side Witness filtering (no Drafts)
      videoCountRef.current = canWrite
        ? data.videos.length
        : data.videos.filter(v => v.state !== VideoState.Draft).length;
      reportCount();
    } catch (err: any) {
      setError(err.message || 'Failed to load videos.');
    } finally {
      setVideosLoading(false);
    }
  }, [vaultId, canWrite]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadStorage = useCallback(async () => {
    if (!canWrite) return;
    try {
      const data = await apiGet<VideoStorageResponse>(`/api/vault/${vaultId}/legacy-videos/storage`);
      setStorage(data);
    } catch {
      // Storage meter is non-critical; fail silently
    }
  }, [vaultId, canWrite]);

  useEffect(() => {
    loadVideos();
    loadStorage();
  }, [loadVideos, loadStorage]);

  async function handleVideoReorder(reordered: VideoResponse[]) {
    setVideos(reordered);
    try {
      await apiPatch(`/api/vault/${vaultId}/legacy-videos/reorder`, {
        orderedIds: reordered.map(v => v.id),
      });
    } catch {
      await loadVideos();
    }
  }

  // ── Card expand / playback ──────────────────────────────────────────────────

  // Owner/Spouse can watch anything. Everyone else only once a video is unsealed —
  // the server enforces this too; here we just avoid asking for a link we won't get.
  function canWatch(v: VideoResponse) {
    return canWrite || v.state === VideoState.Unsealed;
  }

  async function toggleExpand(videoId: string) {
    const isExpanding = !expandedIds.has(videoId);
    setExpandedIds(prev => {
      const next = new Set(prev);
      isExpanding ? next.add(videoId) : next.delete(videoId);
      return next;
    });

    // Lazily fetch playback URL on first expand
    if (isExpanding && !playbackUrls[videoId]) {
      const video = videos.find(v => v.id === videoId);
      if (!video?.isUploadComplete || !canWatch(video)) return;
      setPlaybackLoading(videoId);
      try {
        const detail = await apiGet<VideoResponse>(`/api/vault/${vaultId}/legacy-videos/${videoId}`);
        if (detail.playbackUrl) {
          setPlaybackUrls(prev => ({ ...prev, [videoId]: detail.playbackUrl! }));
        }
      } catch {
        // Playback URL load failed; user can still see the card
      } finally {
        setPlaybackLoading(null);
      }
    }
  }

  // ── Upload ──────────────────────────────────────────────────────────────────

  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] ?? null;
    if (!file) return;
    setUploadFile(file);
    // Auto-fill title from filename (strip extension)
    if (!uploadTitle) {
      const nameNoExt = file.name.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' ');
      setUploadTitle(nameNoExt.trim());
    }
    // Per-file size check — catch immediately on file select, not at upload time
    if (file.size > MAX_VIDEO_BYTES) {
      setUploadError(`This file is ${formatBytes(file.size)}. The maximum size per video is 1 GB. Please trim the video or use a lower resolution.`);
      setUploadQuotaExceeded(false);
      return;
    }

    // Early quota check — catch overage before the user even clicks Upload
    if (storage) {
      const remaining = storage.quotaBytes - storage.usedBytes;
      if (file.size > remaining) {
        setUploadQuotaExceeded(true);
        setUploadError('');
        return;
      }
    }
    setUploadQuotaExceeded(false);
    setUploadError('');
  }

  async function handleUpload(e: React.FormEvent) {
    e.preventDefault();
    if (!uploadFile || !uploadTitle.trim()) {
      setUploadError('Please select a file and enter a title.');
      return;
    }
    if (!uploadFile.type.startsWith('video/')) {
      setUploadError('Please select a video file (.mp4, .mov, .webm, or .mkv).');
      return;
    }
    if (uploadFile.size > MAX_VIDEO_BYTES) {
      setUploadError(`File is ${formatBytes(uploadFile.size)}. The maximum allowed size is 1 GB.`);
      return;
    }
    if (storage) {
      const remaining = storage.quotaBytes - storage.usedBytes;
      if (uploadFile.size > remaining) {
        setUploadQuotaExceeded(true);
        return;
      }
    }

    setUploading(true);
    setUploadProgress(0);
    setUploadError('');

    try {
      // Step 1: Get SAS upload token
      const tokenReq: RequestVideoUploadTokenRequest = {
        fileName: uploadFile.name,
        contentType: uploadFile.type,
        fileSizeBytes: uploadFile.size,
        title: uploadTitle.trim(),
        description: uploadDesc.trim() || null,
      };
      const token = await apiPost<VideoUploadTokenResponse>(
        `/api/vault/${vaultId}/legacy-videos/upload-token`, tokenReq
      );

      // Step 2: Upload directly to Azure Blob Storage via SAS URL
      setUploadProgress(1); // show bar immediately
      await doXhrUpload(token.sasUrl, uploadFile, setUploadProgress);

      // Step 3: Confirm the upload is complete
      await apiPost<VideoResponse>(
        `/api/vault/${vaultId}/legacy-videos/${token.videoId}/confirm-upload`
      );

      // Reset and reload
      setShowUpload(false);
      setUploadFile(null);
      setUploadTitle('');
      setUploadDesc('');
      if (fileRef.current) fileRef.current.value = '';
      await Promise.all([loadVideos(), loadStorage()]);
    } catch (err: any) {
      setUploadError(err.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  }

  // ── State transitions ───────────────────────────────────────────────────────

  async function handleSeal(videoId: string) {
    setActionInProgress(videoId);
    try {
      const updated = await apiPost<VideoResponse>(
        `/api/vault/${vaultId}/legacy-videos/${videoId}/seal`
      );
      setVideos(prev => prev.map(v => v.id === videoId ? updated : v));
    } catch (err: any) {
      setError(err.message || 'Failed to seal video.');
    } finally {
      setActionInProgress(null);
    }
  }

  async function handleUnseal(videoId: string) {
    setActionInProgress(videoId);
    try {
      const updated = await apiPost<VideoResponse>(
        `/api/vault/${vaultId}/legacy-videos/${videoId}/unseal`
      );
      setVideos(prev => prev.map(v => v.id === videoId ? updated : v));
    } catch (err: any) {
      setError(err.message || 'Failed to unseal video.');
    } finally {
      setActionInProgress(null);
    }
  }

  async function handleShare(videoId: string) {
    setActionInProgress(videoId);
    try {
      const updated = await apiPost<VideoResponse>(
        `/api/vault/${vaultId}/legacy-videos/${videoId}/share`
      );
      setVideos(prev => prev.map(v => v.id === videoId ? updated : v));
    } catch (err: any) {
      setError(err.message || 'Failed to share video.');
    } finally {
      setActionInProgress(null);
    }
  }

  async function handleUnshare(videoId: string) {
    setActionInProgress(videoId);
    try {
      const updated = await apiPost<VideoResponse>(
        `/api/vault/${vaultId}/legacy-videos/${videoId}/unshare`
      );
      setVideos(prev => prev.map(v => v.id === videoId ? updated : v));
    } catch (err: any) {
      setError(err.message || 'Failed to make video private.');
    } finally {
      setActionInProgress(null);
    }
  }

  // ── Edit title / description ────────────────────────────────────────────────

  function startEdit(video: VideoResponse) {
    setEditingId(video.id);
    setEditTitle(video.title);
    setEditDesc(video.description ?? '');
    setEditError('');
  }

  async function handleSaveEdit(e: React.FormEvent, videoId: string) {
    e.preventDefault();
    if (!editTitle.trim()) {
      setEditError('Title is required.');
      return;
    }
    setEditSaving(true);
    setEditError('');
    try {
      const updated = await apiPut<VideoResponse>(
        `/api/vault/${vaultId}/legacy-videos/${videoId}`,
        { title: editTitle.trim(), description: editDesc.trim() || null }
      );
      setVideos(prev => prev.map(v => v.id === videoId ? updated : v));
      setEditingId(null);
    } catch (err: any) {
      setEditError(err.message || 'Failed to save changes.');
    } finally {
      setEditSaving(false);
    }
  }

  // ── Voting ──────────────────────────────────────────────────────────────────

  async function handleVote(videoId: string, vote: boolean) {
    setActionInProgress(videoId);
    try {
      const updated = await apiPost<VideoResponse>(
        `/api/vault/${vaultId}/legacy-videos/${videoId}/vote`, { vote }
      );
      setVideos(prev => prev.map(v => v.id === videoId ? updated : v));
    } catch (err: any) {
      setError(err.message || 'Failed to cast vote.');
    } finally {
      setActionInProgress(null);
    }
  }

  async function handleRetractVote(videoId: string) {
    setActionInProgress(videoId);
    try {
      await apiDelete(`/api/vault/${vaultId}/legacy-videos/${videoId}/vote`);
      await loadVideos();
    } catch (err: any) {
      setError(err.message || 'Failed to retract vote.');
    } finally {
      setActionInProgress(null);
    }
  }

  // ── Delete ──────────────────────────────────────────────────────────────────

  async function handleDelete(video: VideoResponse) {
    setDeleting(true);
    try {
      await apiDelete(`/api/vault/${vaultId}/legacy-videos/${video.id}`);
      setConfirmDelete(null);
      await Promise.all([loadVideos(), loadStorage()]);
    } catch (err: any) {
      setError(err.message || 'Delete failed.');
      setConfirmDelete(null);
    } finally {
      setDeleting(false);
    }
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  // Mirror server-side Witness behaviour: Witnesses/Advisors never see Draft videos.
  // In preview mode canWrite is false, so this filters Drafts out of the display.
  const displayedVideos = canWrite ? videos : videos.filter(v => v.state !== VideoState.Draft);
  const searching = query.trim() !== '';
  const visibleVideos = filterVideos(displayedVideos, query);

  const storageWarn = storage != null && storage.usedPercent > 90;

  return (
    <div className="legacy-messages-wrap">

      {/* ── Tab bar ────────────────────────────────────────────────────────── */}
      <div className="lmv-tab-bar">
        <button
          className={`lmv-tab${activeTab === 'videos' ? ' lmv-tab--active' : ''}`}
          onClick={() => setActiveTab('videos')}
        >
          Videos
          {displayedVideos.length > 0 && (
            <span className="lmv-tab-badge">{displayedVideos.length}</span>
          )}
        </button>
        <button
          className={`lmv-tab${activeTab === 'letters' ? ' lmv-tab--active' : ''}`}
          onClick={() => setActiveTab('letters')}
        >
          Letters
          {letterCount > 0 && (
            <span className="lmv-tab-badge">{letterCount}</span>
          )}
        </button>
      </div>

      {/* ── Video tab ──────────────────────────────────────────────────────── */}
      {activeTab === 'videos' && (
      <div className="lmv-section">

        <div className="lmv-section-header">
          <div>
            <h3 className="lmv-section-title">Legacy Videos</h3>
            <p className="lmv-section-intro">
              Keep your family's videos in one place — share them with the family
              right away, or seal one to be opened when the time is right.
              Supports MP4, MOV, WebM, and MKV up to 1 GB.
            </p>
          </div>
          {canWrite && !uploading && (
            <button className="btn-text lmv-add-btn"
                    onClick={() => setShowUpload(v => !v)}>
              {showUpload ? 'Cancel' : '+ Add Video'}
            </button>
          )}
        </div>

        {/* Storage meter */}
        {canWrite && storage && (
          <div className="lmv-storage-row">
            <div className="lmv-storage-labels">
              <span className="lmv-storage-used">{storage.usedFormatted} used</span>
              <span className="lmv-storage-quota">of {storage.quotaFormatted}</span>
            </div>
            <div className="lmv-meter-track">
              <div
                className={`lmv-meter-fill${storageWarn ? ' lmv-meter-fill--warning' : ''}`}
                style={{ width: `${Math.min(storage.usedPercent, 100)}%` }}
              />
            </div>
            {storageWarn && (
              <p className="lmv-storage-warn">
                You're using {Math.round(storage.usedPercent)}% of your storage.
                Delete old videos or contact support to increase your limit.
              </p>
            )}
          </div>
        )}

        <AlertMessage type="error" message={error} />

        {/* Upload form */}
        {canWrite && showUpload && (
          <form className="lmv-upload-form" onSubmit={handleUpload}>
            {uploading ? (
              <div className="lmv-upload-progress-wrap">
                <p className="lmv-upload-progress-label">
                  Uploading to your vault… {uploadProgress}%
                </p>
                <div className="lmv-upload-progress-track">
                  <div className="lmv-upload-progress-fill"
                       style={{ width: `${uploadProgress}%` }} />
                </div>
                <p className="lmv-upload-progress-hint">
                  Please keep this page open until the upload completes.
                </p>
              </div>
            ) : (
              <>
                {uploadQuotaExceeded && (
                  <div className="lmv-quota-upsell">
                    <p className="lmv-quota-upsell-msg">
                      <strong>You've reached your {storage?.quotaFormatted ?? ''} storage limit.</strong>{' '}
                      Your family's memories are worth more room.{' '}
                      <a
                        href={`mailto:support@cedarvaultapp.com?subject=Storage%20Upgrade%20Request&body=Hi%2C%20I%20would%20like%20to%20increase%20the%20video%20storage%20on%20my%20Cedar%20Vault%20account.`}
                        className="lmv-quota-upsell-link"
                      >
                        Contact us to upgrade →
                      </a>
                    </p>
                    <p className="lmv-quota-upsell-hint">
                      You can also free up space by deleting a video below.
                    </p>
                  </div>
                )}
                {uploadError && <AlertMessage type="error" message={uploadError} />}
                <div className="lmv-upload-row">
                  <div className="form-group lmv-upload-file-group">
                    <label className="form-label" htmlFor="lmvFile">Video file</label>
                    <input
                      id="lmvFile"
                      ref={fileRef}
                      type="file"
                      className="form-input"
                      accept={ACCEPTED_TYPES}
                      onChange={handleFileSelect}
                      required
                    />
                    {uploadFile && (
                      <p className="lmv-file-info">
                        {uploadFile.name} — {formatBytes(uploadFile.size)}
                      </p>
                    )}
                  </div>
                  <div className="form-group lmv-upload-title-group">
                    <label className="form-label" htmlFor="lmvTitle">Title *</label>
                    <input
                      id="lmvTitle"
                      className="form-input"
                      type="text"
                      value={uploadTitle}
                      onChange={e => setUploadTitle(e.target.value)}
                      maxLength={200}
                      placeholder="e.g., Message to My Children"
                      required
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="lmvDesc">Description (optional)</label>
                  <textarea
                    id="lmvDesc"
                    className="form-input lmv-desc-input"
                    value={uploadDesc}
                    onChange={e => setUploadDesc(e.target.value)}
                    maxLength={1000}
                    rows={3}
                    placeholder="Add a note about this video…"
                  />
                </div>
                <div className="lmv-upload-actions">
                  <button className="btn-primary" type="submit">Upload Video</button>
                  <button className="btn-text" type="button"
                          onClick={() => { setShowUpload(false); setUploadFile(null); setUploadTitle(''); setUploadDesc(''); setUploadError(''); setUploadQuotaExceeded(false); }}>
                    Cancel
                  </button>
                </div>
              </>
            )}
          </form>
        )}

        {/* Video cards */}
        {videosLoading ? (
          <LoadingSpinner />
        ) : displayedVideos.length === 0 ? (
          <EmptyState
            icon="letters"
            title="No videos yet"
            subtitle={canWrite
              ? 'Record a message for your family — add your first video above.'
              : 'No videos have been shared with this vault yet.'}
          />
        ) : (
          <div className="lmv-cards">
            {displayedVideos.length > 3 && (
              <div className="lmv-search-row">
                <input
                  type="search"
                  className="form-input lmv-search"
                  aria-label="Search videos"
                  placeholder="Search titles and descriptions…"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                />
                {searching && (
                  <span className="lmv-search-count">
                    Showing {visibleVideos.length} of {displayedVideos.length}
                  </span>
                )}
              </div>
            )}
            {visibleVideos.length === 0 && (
              <p className="lmv-no-match">
                No videos match “{query.trim()}”. Try fewer words.
              </p>
            )}
            {/* Reordering a filtered list would write a partial order — only allow it on the full list. */}
            <SortableList
              items={visibleVideos}
              canReorder={canWrite && !searching}
              onReorder={handleVideoReorder}
              renderItem={(video) => {
              const isDraft    = video.state === VideoState.Draft;
              const isSealed   = video.state === VideoState.Sealed;
              const isUnsealed = video.state === VideoState.Unsealed;
              const isShared   = isSharedVideo(video);
              const isExpanded = expandedIds.has(video.id);
              const isMine     = video.isOwnedByCurrentUser;
              const busy       = actionInProgress === video.id;
              const playUrl    = playbackUrls[video.id] ?? null;
              const loadingUrl = playbackLoading === video.id;

              const cardMod = isDraft ? 'lmv-card--draft'
                            : isUnsealed ? 'lmv-card--unsealed'
                            : 'lmv-card--sealed';

              return (
                <div className={`lmv-card ${cardMod}`}>

                  {/* ── Card header ─────────────────────────────────────── */}
                  <div className="lmv-card-header" onClick={() => toggleExpand(video.id)}>
                    <div className="lmv-card-top">
                      <div className="lmv-card-lock">
                        {isDraft    && <DraftVideoIcon />}
                        {isSealed   && <SealedVideoIcon />}
                        {isUnsealed && <UnsealedVideoIcon />}
                        <span className={`lmv-state-badge lmv-state--${isDraft ? 'draft' : isUnsealed ? 'unsealed' : 'sealed'}`}>
                          {isDraft ? 'Draft' : isShared ? 'Shared' : isUnsealed ? 'Unsealed' : 'Sealed'}
                        </span>
                      </div>
                      <span className="lmv-card-author">{video.createdByName ?? 'Unknown'}</span>
                    </div>

                    <div className="lmv-card-title">{video.title}</div>

                    {!isExpanded && video.description && (
                      <p className="lmv-card-preview">{video.description}</p>
                    )}

                    <div className="lmv-card-meta">
                      <span className="lmv-card-filesize">{formatBytes(video.fileSizeBytes)}</span>
                      <span className="lmv-card-date">
                        {isUnsealed && video.unsealedAt
                          ? `${isShared ? 'Shared' : 'Unsealed'} ${formatDate(video.unsealedAt)}`
                          : isSealed && video.sealedAt
                            ? `Sealed ${formatDate(video.sealedAt)}`
                            : `Added ${formatDate(video.createdAt)}`}
                      </span>
                    </div>

                    {isSealed && (
                      <VideoVoteBar yesVotes={video.yesVotes} totalMembers={video.totalMembers} />
                    )}

                    <span className="lmv-chevron" aria-hidden="true">
                      {isExpanded ? '▲' : '▼'}
                    </span>
                  </div>

                  {/* ── Expanded body ───────────────────────────────────── */}
                  {isExpanded && (
                    <div className="lmv-card-body">

                      {video.description && (
                        <p className="lmv-card-desc">{video.description}</p>
                      )}

                      {isShared && (
                        <p className="lmv-shared-note">
                          Shared with everyone in this vault, including an Advisor.
                        </p>
                      )}

                      {/* Sealed videos can't be watched by Witnesses/Advisors until unsealed */}
                      {video.isUploadComplete && !canWatch(video) && (
                        <p className="lmv-sealed-note">
                          This video is sealed. It can be watched once the family votes to open it.
                        </p>
                      )}

                      {/* Video player */}
                      {video.isUploadComplete && canWatch(video) && (
                        <div className="lmv-player-wrap">
                          {loadingUrl ? (
                            <div className="lmv-player-loading"><LoadingSpinner /></div>
                          ) : playUrl ? (
                            <video
                              className="lmv-video-player"
                              controls
                              playsInline
                              preload="metadata"
                              src={playUrl}
                            />
                          ) : (
                            <div className="lmv-player-loading">
                              <p className="lmv-player-error">Could not load playback URL.</p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Vote section — Sealed, non-author */}
                      {isSealed && (
                        <div className="lmv-vote-section">
                          {video.votes.length > 0 && (
                            <div className="lmv-vote-list">
                              {video.votes.map(v => (
                                <span key={v.id}
                                      className={`lmv-vote-chip ${v.vote ? 'lmv-vote-chip--yes' : 'lmv-vote-chip--no'}`}>
                                  {v.memberName ?? 'Member'}: {v.vote ? 'Unseal' : 'Keep sealed'}
                                </span>
                              ))}
                            </div>
                          )}

                          {userRole !== VaultRole.Advisor && !isMine && (
                            <div className="lmv-vote-actions">
                              {video.currentUserVote === null ? (
                                <>
                                  <button className="lmv-vote-btn lmv-vote-btn--yes"
                                          onClick={() => handleVote(video.id, true)}
                                          disabled={busy}>
                                    {busy ? <LoadingSpinner small /> : 'Vote to Unseal'}
                                  </button>
                                  <button className="lmv-vote-btn lmv-vote-btn--no"
                                          onClick={() => handleVote(video.id, false)}
                                          disabled={busy}>
                                    Keep Sealed
                                  </button>
                                </>
                              ) : (
                                <>
                                  <span className="lmv-my-vote">
                                    Your vote: <strong>{video.currentUserVote ? 'Unseal' : 'Keep sealed'}</strong>
                                  </span>
                                  <button className="btn-text"
                                          onClick={() => handleRetractVote(video.id)}
                                          disabled={busy}>
                                    Change vote
                                  </button>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* ── Edit form ───────────────────────────────────────── */}
                  {canWrite && editingId === video.id && (
                    <form className="lmv-edit-form" onSubmit={e => handleSaveEdit(e, video.id)}>
                      {editError && <AlertMessage type="error" message={editError} />}
                      <div className="form-group">
                        <label className="form-label" htmlFor={`lmvEditTitle-${video.id}`}>Title</label>
                        <input
                          id={`lmvEditTitle-${video.id}`}
                          className="form-input"
                          type="text"
                          value={editTitle}
                          onChange={e => setEditTitle(e.target.value)}
                          maxLength={200}
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label" htmlFor={`lmvEditDesc-${video.id}`}>Description</label>
                        <textarea
                          id={`lmvEditDesc-${video.id}`}
                          className="form-input lmv-desc-input"
                          value={editDesc}
                          onChange={e => setEditDesc(e.target.value)}
                          maxLength={1000}
                          rows={4}
                          placeholder="Who is in it, where and when, what it's about…"
                        />
                        <p className="lmv-edit-count">{editDesc.length} / 1000</p>
                      </div>
                      <div className="lmv-upload-actions">
                        <button className="btn-primary" type="submit" disabled={editSaving}>
                          {editSaving ? 'Saving…' : 'Save changes'}
                        </button>
                        <button className="btn-text" type="button"
                                onClick={() => setEditingId(null)} disabled={editSaving}>
                          Cancel
                        </button>
                      </div>
                    </form>
                  )}

                  {/* ── Action bar (Owner/Spouse) ───────────────────────── */}
                  {canWrite && (
                    <div className="lmv-card-actions">
                      {isMine && isDraft && (
                        <>
                          <span className="lmv-draft-saved-note">✓ Saved as draft</span>
                          <button className="vh-action-link lmv-share-btn"
                                  title="Everyone in this vault, including an Advisor, will be able to watch it."
                                  onClick={() => handleShare(video.id)} disabled={busy}>
                            {busy ? 'Sharing…' : 'Share with family'}
                          </button>
                          <button className="vh-action-link lmv-seal-btn"
                                  onClick={() => handleSeal(video.id)} disabled={busy}>
                            {busy ? 'Sealing…' : '🔒 Seal when ready'}
                          </button>
                          <button className="vh-action-link lmv-delete-btn"
                                  onClick={() => setConfirmDelete(video)}>
                            Delete
                          </button>
                        </>
                      )}
                      {isMine && isSealed && (
                        <button className="vh-action-link"
                                onClick={() => handleUnseal(video.id)} disabled={busy}>
                          {busy ? 'Unsealing…' : 'Unseal'}
                        </button>
                      )}
                      {isMine && isShared && (
                        <button className="vh-action-link"
                                onClick={() => handleUnshare(video.id)} disabled={busy}>
                          {busy ? 'Working…' : 'Make private again'}
                        </button>
                      )}
                      {isMine && isUnsealed && !isShared && (
                        <button className="vh-action-link lmv-seal-btn"
                                onClick={() => handleSeal(video.id)} disabled={busy}>
                          {busy ? 'Resealing…' : '🔒 Re-Seal Video'}
                        </button>
                      )}
                      {editingId !== video.id && (
                        <button className="vh-action-link lmv-edit-btn"
                                onClick={() => startEdit(video)}>
                          Edit details
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            }}
            />
          </div>
        )}
      </div>
      )}

      {/* ── Letters tab ────────────────────────────────────────────────────── */}
      {activeTab === 'letters' && (
        <LettersList
          vaultId={vaultId}
          userRole={userRole}
          onCountChange={count => {
            letterCountRef.current = count;
            setLetterCount(count);
            reportCount();
          }}
        />
      )}

      {/* ── Delete confirmation ─────────────────────────────────────────────── */}
      {confirmDelete && (
        <div className="modal-overlay" onClick={() => !deleting && setConfirmDelete(null)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <h3>Delete this video?</h3>
            <p>
              <strong>"{confirmDelete.title}"</strong> will be permanently deleted
              and cannot be recovered.
            </p>
            <div className="modal-actions">
              <button className="btn-danger"
                      onClick={() => handleDelete(confirmDelete)}
                      disabled={deleting}>
                {deleting ? <LoadingSpinner small /> : 'Delete Video'}
              </button>
              <button className="btn-text" onClick={() => setConfirmDelete(null)}
                      disabled={deleting}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
